import React, { useState } from 'react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import PricingFormulaHelpModal from '../../../../ui/overlays/modals/PricingFormulaHelpModal';
import type { BaseSectionProps, WorkflowCategory, PipelineMode } from './index';
import { MULTI_TASK_TYPES } from '../../../../../utils/multiTaskTypes';

export default function GeneralSection({
  formData,
  onFormDataChange,
  workflow,
  language
}: BaseSectionProps) {
  const t = useLocalization(language);
  const [showPricingHelp, setShowPricingHelp] = useState(false);

  const categories: WorkflowCategory[] = ['Image', 'Video', 'Voice', '3D', 'Text'];
  const actionButtonTypes = ['generate', 'upscale', 'process', 'create'] as const;
  const inputFilesLogicOptions = ['single_per_request', 'all_per_request'] as const;
  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name={ICONS.INFO} size={20} className="text-blue-400" />
          {t.admin?.basicInfo || 'Basic Information'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Visual ID */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              {t.admin?.workflowId || 'Workflow ID'}
            </label>
            <input
              type="text"
              value={formData.visual_id}
              onChange={(e) => onFormDataChange('visual_id', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="e.g., flux-pro-v2"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t.admin?.workflowIdDesc || 'Unique identifier for URL routing'}
            </p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              {t.admin?.workflowTitle || 'Title'}
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => onFormDataChange('title', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Workflow title"
            />
          </div>
        </div>
      </section>

      {/* Descriptions */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name={ICONS.PROMPTS} size={20} className="text-purple-400" />
          {t.admin?.descriptions || 'Descriptions'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Russian */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <span className="text-lg">🇷🇺</span>
              {t.admin?.descriptionRu || 'Description (Russian)'}
            </label>
            <textarea
              value={formData.description_ru}
              onChange={(e) => onFormDataChange('description_ru', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
              placeholder="Описание на русском"
            />
          </div>

          {/* English */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <span className="text-lg">🇬🇧</span>
              {t.admin?.descriptionEn || 'Description (English)'}
            </label>
            <textarea
              value={formData.description_en}
              onChange={(e) => onFormDataChange('description_en', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none"
              placeholder="Description in English"
            />
          </div>
        </div>
      </section>

      {/* Configuration */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name={ICONS.SETTINGS} size={20} className="text-green-400" />
          {t.admin?.configuration || 'Configuration'}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.IMAGE} size={16} className="text-gray-400" />
              {t.admin?.category || 'Category'}
            </label>
            <select
              value={formData.category}
              onChange={(e) => onFormDataChange('category', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Action Button Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.PLAY} size={16} className="text-gray-400" />
              {t.admin?.actionButtonType || 'Action Button Type'}
            </label>
            <select
              value={formData.action_button_type}
              onChange={(e) => onFormDataChange('action_button_type', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              {actionButtonTypes.map(type => (
                <option key={type} value={type}>
                  {t.workflow?.actions?.[type] || type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Input Files Logic */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.UPLOAD} size={16} className="text-gray-400" />
              {t.admin?.inputFilesLogic || 'Input Files Logic'}
            </label>
            <select
              value={formData.input_files_logic}
              onChange={(e) => onFormDataChange('input_files_logic', e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="single_per_request">
                {t.admin?.singlePerRequest || 'Single per request'}
              </option>
              <option value="all_per_request">
                {t.admin?.allPerRequest || 'All per request'}
              </option>
            </select>
          </div>

          {/* Multi Task Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.LAYERS} size={16} className="text-gray-400" />
              {t.admin?.multiTaskType?.label || 'Multi task type'}
            </label>
            <select
              value={formData.multi_task_type || ''}
              onChange={(e) => onFormDataChange('multi_task_type', e.target.value === '' ? null : e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="">{t.admin?.multiTaskType?.none || 'None'}</option>
              {MULTI_TASK_TYPES.map(type => (
                <option key={type} value={type}>
                  {t.admin?.multiTaskType?.[type] || type}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {t.admin?.multiTaskType?.help || 'Makes this model available in the Multi section for parallel generation.'}
            </p>
          </div>

          {/* Availability */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.USERS} size={16} className="text-gray-400" />
              {t.admin?.availability || 'Availability'}
            </label>
            <select
              value={formData.is_available_for_all ? 'public' : 'restricted'}
              onChange={(e) => onFormDataChange('is_available_for_all', e.target.value === 'public')}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="public">{t.workflow?.public || 'Public'}</option>
              <option value="restricted">{t.workflow?.restricted || 'Restricted'}</option>
            </select>
          </div>

          {/* NSFW Content - Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.WARNING} size={16} className="text-gray-400" />
              {t.promptsV2?.nsfw?.label || 'NSFW Content'}
            </label>
            <select
              value={formData.is_nsfw ? 'nsfw' : 'safe'}
              onChange={(e) => onFormDataChange('is_nsfw', e.target.value === 'nsfw')}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="safe">{t.common?.safe || 'Safe'}</option>
              <option value="nsfw">NSFW</option>
            </select>
            {formData.is_nsfw && (
              <p className="mt-2 text-xs text-orange-400">
                {t.promptsV2?.nsfw?.description || 'This workflow is marked as adult content and will only be visible to organizations with NSFW enabled.'}
              </p>
            )}
          </div>

          {/* Display Status */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.INFO} size={16} className="text-gray-400" />
              {t.admin?.displayStatus || 'Display Status'}
            </label>
            <select
              value={formData.display_status || 'active'}
              onChange={(e) => onFormDataChange('display_status', e.target.value as 'active' | 'new' | 'archived')}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="active">{t.admin?.displayStatusActive || 'Active'}</option>
              <option value="new">{t.admin?.displayStatusNew || 'New'}</option>
              <option value="archived">{t.admin?.displayStatusArchived || 'Archived'}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {formData.display_status === 'new'
                ? (t.admin?.displayStatusNewDesc || 'Shown at the top of the tile selector with a "New" badge')
                : formData.display_status === 'archived'
                  ? (t.admin?.displayStatusArchivedDesc || 'Hidden by default, shown grayed out when "Show archived" is enabled')
                  : (t.admin?.displayStatusActiveDesc || 'Standard visibility in the workflow selector')
              }
            </p>
          </div>

          {/* Pipeline Type - Tri-state selector */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
              <Icon name={ICONS.LAYERS} size={16} className="text-gray-400" />
              {t.admin?.pipelineType?.label || 'Pipeline Type'}
            </label>
            <select
              value={formData.settings?.pipeline_mode || (formData.settings?.auto_prompt_enhance ? 'auto_prompt_enhance' : 'off')}
              onChange={(e) => {
                const mode = e.target.value as PipelineMode;
                const newSettings = {
                  ...(formData.settings || {}),
                  pipeline_mode: mode,
                  auto_prompt_enhance: mode === 'auto_prompt_enhance'
                };
                onFormDataChange('settings', newSettings);
              }}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="off">{t.admin?.pipelineType?.off || 'Off'}</option>
              <option value="auto_prompt_enhance">
                {t.admin?.pipelineType?.autoPromptEnhance || 'Auto Prompt Enhance'} ({t.admin?.pipelineType?.deprecated || 'deprecated'})
              </option>
              <option value="pipeline">{t.admin?.pipelineType?.pipeline || 'Pipeline'}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {formData.settings?.pipeline_mode === 'pipeline'
                ? (t.admin?.pipelineType?.pipelineDescription || 'Multi-step pipeline execution')
                : formData.settings?.pipeline_mode === 'auto_prompt_enhance' || (!formData.settings?.pipeline_mode && formData.settings?.auto_prompt_enhance)
                  ? (t.admin?.pipelineType?.autoPromptEnhanceDescription || 'Enhance prompts before generation (deprecated)')
                  : (t.admin?.pipelineType?.offDescription || 'Standard execution via Dify Endpoints')
              }
            </p>
          </div>

          {/* No Refund on Content Policy */}
          <div className="col-span-full">
            <div className="flex items-start gap-3 bg-gray-800/50 border border-gray-700 rounded-lg p-3">
              <input
                id="no-refund-content-policy"
                type="checkbox"
                checked={formData.settings?.no_refund_on_content_policy || false}
                onChange={(e) => {
                  const newSettings = {
                    ...(formData.settings || {}),
                    no_refund_on_content_policy: e.target.checked
                  };
                  onFormDataChange('settings', newSettings);
                }}
                className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
              />
              <div>
                <label htmlFor="no-refund-content-policy" className="text-sm font-medium text-gray-300 cursor-pointer">
                  {t.admin?.noRefundContentPolicy?.label || 'No refund on content policy violation'}
                </label>
                <p className="mt-0.5 text-xs text-gray-500">
                  {t.admin?.noRefundContentPolicy?.description || 'Do not refund tokens when content violates provider policy (e.g. xAI/Grok)'}
                </p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Pricing Formula */}
      <section>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name={ICONS.HISTORY} size={20} className="text-yellow-400" />
          {t.admin?.pricingFormula || 'Pricing Formula'}
          <button
            type="button"
            onClick={() => setShowPricingHelp(true)}
            className="ml-1 p-1 text-gray-400 hover:text-yellow-400 hover:bg-gray-800 rounded-full transition-colors"
            title={t.admin?.pricingFormulaHelp?.title || 'Help'}
          >
            <Icon name={ICONS.HELP} size={16} />
          </button>
        </h3>

        <div className="bg-gradient-to-br from-yellow-900/30 to-purple-900/20 border border-yellow-500/40 rounded-xl p-4">
          <textarea
            value={formData.pricing_formula}
            onChange={(e) => onFormDataChange('pricing_formula', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 bg-gray-800/80 border border-gray-600 rounded-lg text-white font-mono text-sm placeholder-gray-500 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 resize-none"
            placeholder="e.g., 100 * num_images"
          />
          <div className="mt-3 text-xs text-gray-400 space-y-1">
            <p><strong>{t.admin?.pricingFormulaHelp?.autoVariablesTitle || 'Auto-calculated'}:</strong> <span className="font-mono text-purple-300">input_img_mpx, target_img_mpx, target_img_width, target_img_height, target_long_side, target_short_side, target_aspect_ratio, audio_duration, video_duration, audio_duration_exact, video_duration_exact, audio_duration_exact_may_zero, video_duration_exact_may_zero, video_width, video_height, video_mpx, prompt_length_1k, prompt_length_1k_may_zero, prompt_length_ceil_100, prompt_length_ceil_100_may_zero, text_length_ceil_100, text_length_ceil_100_may_zero, has_prompt</span></p>
            <p className="text-gray-500">{t.admin?.pricingFormulaHelp?.dynamicVariablesDesc || 'Any parameter from workflow inputs can be used'}</p>
            <p><strong>{t.admin?.examples || 'Examples'}:</strong></p>
            <p className="font-mono text-gray-300">100 * num_images</p>
            <p className="font-mono text-gray-300">10 + (video_mpx * 50) + (video_duration * 5)</p>
          </div>
        </div>
      </section>

      {/* Pricing Formula Help Modal */}
      <PricingFormulaHelpModal
        isOpen={showPricingHelp}
        onClose={() => setShowPricingHelp(false)}
        t={t}
      />
    </div>
  );
}
